import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { AggregateScatterPoint } from "@/types";
import { Divider } from "@mui/material";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";

interface AggregateMonthlyFactorScatterChartProps {
  data: AggregateScatterPoint[];
}

export const AggregateMonthlyFactorScatterChart = ({ data: rawData }: AggregateMonthlyFactorScatterChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const data = rawData
    .filter((d) => d.group_size > 0)
    .map((d) => ({
      x: d.monthly_factor,
      y: d.group_size,
      carrier: d.carrier_name,
      policyholder: d.client_name,
    }));

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  const carriers = [...new Set(data.map((d) => d.carrier))];

  const getColor = (carrier: string) => {
    const index = carriers.indexOf(carrier);
    return chartPalette[index % chartPalette.length];
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis
          type="number"
          dataKey="x"
          name="Monthly Aggregate Factor"
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis type="number" dataKey="y" name="Group Size" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <Tooltip
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const item = payload[0].payload;
            return (
              <div
                style={{
                  ...getChartTooltipStyle(theme),
                  padding: "8px",
                }}
              >
                <strong>{item.policyholder}</strong>
                <div>{item.carrier}</div>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <div>Monthly Aggregate Factor: {formatUSD(item.x, currencyCompactNotation)}</div>
                <div>Group Size: {item.y.toLocaleString()}</div>
              </div>
            );
          }}
        />
        <Scatter data={data}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.carrier)} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
